<template>
    <div class="nav-container">
        <nav class="navbar">
            <a href="/admin" class="logo" title="Logged in as admin">
                <MaltImg src="/rebranding2020/malt-logo/malt-yellow" width="125" height="36" alt="" />
                <span>Admin</span>
            </a>
            <ul class="nav-links">
                <li v-for="section in filteredSections" :key="section.name" class="nav-item">
                    <a
                        :href="section.link ? section.link : '#'"
                        :class="section.links.length > 0 ? 'with-caret' : ''"
                        role="menuitem"
                        :aria-haspopup="section.links.length > 0 ? 'true' : 'false'"
                    >
                        {{ section.name }}
                    </a>
                    <ul v-if="section.links" class="dropdown" aria-label="submenu">
                        <li v-for="link in section.links" :key="link.name" :class="{separator: link.separator}">
                            <template v-if="link.separator">
                                <hr />
                            </template>
                            <template v-else>
                                <NuxtLink v-if="link.link" :to="appLink(link.app, link.link)" :external="link.app !== app">
                                    {{ link.name }}
                                </NuxtLink>
                                <span v-if="!link.link" class="undone">
                                    {{ link.name }}
                                </span>
                                <span v-if="link.comment">
                                    <small>
                                        {{ link.comment }}
                                    </small>
                                </span>
                            </template>
                        </li>
                    </ul>
                </li>
                <li v-if="logout" class="nav-item">
                    <a href="#" role="menuitem" aria-haspopup="false" @click.prevent="logout">Logout</a>
                </li>
            </ul>
        </nav>
    </div>
</template>

<script setup lang="ts">
    import {onMounted, ref} from 'vue';
    import {MaltImg} from '#components';
    import {useHasEnoughPriviledge, useTranslation} from '#imports';

    const {t} = useTranslation();

    interface Link {
        name: string;
        comment?: string;
        app?: string;
        link: string;
        roles?: string[];
        separator?: true;
    }

    interface Section {
        name: string;
        link?: string;
        links: Link[];
    }

    interface Props {
        /** the name of your app. It will be used internally by the component to know if it's an internal link or not */
        app: string;
        /** the function to call when the user clicks on the logout link */
        logout?: () => void;
    }

    const props = withDefaults(defineProps<Props>(), {
        logout: () => {},
    });

    function appLink(app: string | undefined, link: string): string {
        if (app === undefined || app === props.app) {
            return link;
        }

        return `/${app}${link}`;
    }

    const filteredSections = ref<Section[]>([]);
    onMounted(async () => {
        const sections: Section[] = [
            {
                name: t('admin.menu.members'),
                links: sortedByName([
                    {
                        name: t('admin.menu.members.admin'),
                        link: '/admin/manager',
                    },
                    {
                        name: t('admin.menu.members.sales.management'),
                        link: '/client-settings/admin/manager/migrations',
                    },
                    {
                        name: t('admin.menu.members.finops.management'),
                        link: '/admin/manager/payment-manager-attribution',
                    },
                    {
                        name: t('admin.menu.members.teams'),
                        link: '/admin/manager/teams',
                    },
                    {
                        name: 'API Consumers/Tokens',
                        link: '/admin/consumers',
                    },
                ]),
            },
            {
                name: t('admin.menu.finance'),
                links: sortedByName([
                    {
                        name: t('admin.menu.financing.invoices'),
                        link: '/financing/admin/invoices',
                        roles: ['ROLE_FINANCING'],
                    },
                    {
                        name: t('admin.menu.financing.bnp'),
                        link: '/project-financing-admin/bnp',
                        roles: ['ROLE_BNP_FINANCING'],
                    },
                    {
                        name: t('admin.menu.financing.clients'),
                        link: '/financing/admin/legal-entities',
                        roles: ['ROLE_FINANCING'],
                    },
                    {
                        name: t('admin.menu.financing.paying.entities'),
                        link: '/financing/admin/paying-entities',
                        roles: ['ROLE_UPDATE_FINANCING_PARTNER'],
                    },
                    {
                        name: t('admin.menu.financing.partner.changes'),
                        link: '/financing/support/financing-partner-change-dashboard',
                        roles: ['ROLE_TECHNICAL_SUPPORT'],
                    },
                    {
                        name: t('admin.menu.missions.integrity'),
                        link: '/project-client/admin/integrity-checks',
                    },
                    {
                        name: t('admin.menu.accounting'),
                        app: 'accounting',
                        link: '/admin/accounting',
                        roles: ['ROLE_ERP_ADMINISTRATOR'],
                    },
                    {
                        name: t('admin.menu.urssaf'),
                        app: 'accounting',
                        link: '/admin/accounting/tools/urssaf',
                        roles: ['ROLE_ADMIN', 'ROLE_ADMIN_URSSAF'],
                    },
                    {
                        name: t('admin.menu.project.search'),
                        link: '/project-client/admin',
                    },
                    {
                        name: t('admin.menu.taxes.helper'),
                        app: 'taxes',
                        link: '/admin/calculation',
                    },
                ])
                    .concat([
                        {
                            separator: true,
                        } as Link,
                    ])
                    .concat(
                        sortedByName([
                            {
                                name: t('admin.menu.download.invoices'),
                                app: 'invoicing',
                                link: '/admin/export',
                                roles: ['ROLE_INVOICES'],
                            },
                            {
                                name: t('admin.menu.download.user.invoices'),
                                app: 'invoicing',
                                link: '/admin/invoices-download',
                                roles: ['ROLE_INVOICES'],
                            },
                            {
                                name: t('admin.menu.download.paid-invoices'),
                                app: 'invoicing',
                                link: '/admin/export-paid-invoices',
                                roles: ['ROLE_INVOICES'],
                            },
                            {
                                name: t('admin.menu.manual-invoices'),
                                app: 'invoicing',
                                link: '/admin/manual-invoices',
                                roles: ['ROLE_INVOICES'],
                            },
                        ]),
                    )
                    .concat([
                        {
                            separator: true,
                        } as Link,
                    ])
                    .concat(
                        sortedByName([
                            {
                                name: t('admin.menu.servicelevel'),
                                link: '/client-settings/admin/servicelevel',
                            },
                            {
                                name: t('admin.menu.pricing'),
                                link: '/client-settings/admin/pricing',
                            },
                            {
                                name: t('admin.menu.freelancers.pf.collaborative'),
                                app: 'taxes',
                                link: '/admin/freelancer-annual-dgfip-report',
                            },
                            {
                                name: t('admin.menu.freelancers.income.summary'),
                                app: 'taxes',
                                link: '/admin/freelancer/income-summary',
                            },
                            {
                                name: t('admin.menu.freelancerfees'),
                                link: '/admin/freelancer-fees',
                            },
                            {
                                name: t('admin.menu.client.fees.settings'),
                                link: '/fees/admin/client-companies',
                            },
                            {
                                name: t('admin.menu.sync.payment.reminder'),
                                app: 'payment-reminder',
                                link: '/admin/sync',
                                roles: ['ROLE_INVOICES'],
                            },
                            {
                                name: t('admin.menu.export.payment.reminder'),
                                app: 'payment-reminder',
                                link: '/admin/export',
                                roles: ['ROLE_INVOICES'],
                            },
                            {
                                name: t('admin.menu.invoices.payment.status'),
                                app: 'invoicing',
                                link: '/admin/invoices-payment-status',
                                roles: ['ROLE_INVOICES'],
                            },
                            {
                                name: t('admin.menu.send.to.client.invoices.of.completed.projects'),
                                app: 'invoicing',
                                link: '/admin/completed-project-emails',
                            },
                            {
                                name: t('admin.menu.payment.routing.admin'),
                                link: '/payment-admin/routing-wallet/payIns',
                                roles: ['ROLE_PAYMENT'],
                            },
                            {
                                name: t('admin.menu.paying.entities.admin'),
                                link: '/payment-admin/paying-entities',
                                roles: ['ROLE_PAYMENT'],
                            },
                        ]),
                    )
                    .concat([
                        {
                            separator: true,
                        } as Link,
                    ])
                    .concat(
                        sortedByName([
                            {
                                name: t('admin.menu.einvoicing.draft.invoices'),
                                app: 'invoicing',
                                link: '/admin/draft-invoices',
                            },
                            {
                                name: t('admin.menu.einvoicing.pipeline'),
                                app: 'invoicing',
                                link: '/admin/e-invoicing/einvoicing-pipeline',
                            },
                            {
                                name: t('admin.menu.einvoicing.registered.entities'),
                                app: 'invoicing',
                                link: '/admin/e-invoicing/registered-entities',
                            },
                        ]),
                    ),
            },
            {
                name: t('admin.menu.compliance'),
                links: sortedByName([
                    {
                        name: t('admin.menu.compliance.legal.entities'),
                        link: '/admin/company/legal-entities',
                    },
                    {
                        name: t('admin.menu.compliance.company.legal.information'),
                        link: '/admin/company/company-legal-information',
                    },
                    {
                        name: t('admin.menu.compliance.company.identities'),
                        link: '/admin/company/company-identities',
                    },
                    {
                        name: t('admin.menu.compliance.company.identities.manual.verification'),
                        link: '/admin/company/legal-entities-manual-verification',
                    },
                    {
                        name: t('admin.menu.compliance.legal.agreements'),
                        link: '/admin/company/legal-agreements',
                        roles: ['ROLE_TERMS_AND_CONDITIONS'],
                    },
                ]),
            },
            {
                name: t('admin.menu.content'),
                links: sortedByName([
                    {
                        name: t('admin.menu.content.rfp'),
                        app: 'admin',
                        link: '/job-positions',
                    },
                    {
                        name: t('admin.menu.content.community'),
                        link: '/cm/admin',
                    },
                    {
                        name: t('admin.menu.content.dashboard'),
                        link: '/dashboard',
                        app: 'admin',
                    },
                    {
                        name: t('admin.menu.content.scatman'),
                        link: '/scatman',
                    },
                    {
                        name: t('admin.menu.content.seo'),
                        link: '/scatman/seo',
                    },
                    {
                        name: t('admin.menu.content.barometers'),
                        link: '/barometer/admin',
                    },
                    {
                        name: t('admin.menu.content.nps'),
                        app: 'admin',
                        link: '/nps',
                    },
                    {
                        name: t('admin.menu.content.sitemap'),
                        link: '/sitemap/admin',
                    },
                    {
                        name: t('admin.menu.content.robots'),
                        link: '/robots',
                        app: 'admin',
                    },
                    {
                        name: t('admin.menu.content.notifications'),
                        link: '/notifications',
                        app: 'admin',
                    },
                    {
                        name: t('admin.menu.content.project.conditions'),
                        link: '/legal/project-conditions',
                        app: 'admin',
                        roles: ['ROLE_TERMS_AND_CONDITIONS'],
                    },
                ]),
            },
            {
                name: 'Internationalization',
                links: sortedByName([
                    {
                        name: t('admin.menu.countries'),
                        app: 'admin',
                        link: '/country',
                    },
                    {
                        name: t('admin.menu.ops.lang'),
                        app: 'admin',
                        link: '/l10n',
                    },
                    {
                        name: t('admin.menu.invoicing.locale'),
                        app: 'invoicing',
                        link: '/admin/invoice/locale',
                    },
                ]),
            },
            {
                name: 'Audit',
                links: sortedByName([
                    {
                        name: t('admin.menu.ops.audit'),
                        app: 'admin',
                        link: 'https://malt.cloud.looker.com/dashboards/475',
                    },
                    {
                        name: t('admin.menu.ops.usurpations'),
                        app: 'admin',
                        link: 'https://malt.cloud.looker.com/dashboards/475',
                    },
                ]),
            },
            {
                name: t('admin.menu.ops'),
                links: sortedByName([
                    {
                        name: 'Accounts Deletion',
                        app: 'account',
                        link: '/admin/account-deletion',
                    },
                    {
                        name: t('admin.menu.caches'),
                        app: 'admin',
                        link: '/caches',
                        roles: ['ROLE_TECHNICAL_SUPPORT'],
                    },
                    {
                        name: t('admin.menu.ops.queues'),
                        link: '/queues',
                        app: 'admin',
                        roles: ['ROLE_TECHNICAL_SUPPORT'],
                    },
                    {
                        name: 'Feature Flags',
                        link: '/admin/ff4j/',
                    },
                    {
                        name: 'Feature Flags Audit',
                        link: '/admin/ff4j/audit',
                    },
                    {
                        name: t('admin.menu.ops.schedulers'),
                        app: 'admin',
                        link: '/scheduler',
                        roles: ['ROLE_TECHNICAL_SUPPORT'],
                    },
                    {
                        name: t('admin.menu.ops.versions'),
                        app: 'admin',
                        link: '/versions',
                    },
                    {
                        name: t('admin.menu.ops.search'),
                        link: '/s/admin',
                    },

                    {
                        name: t('admin.menu.ops.ip'),
                        app: 'admin',
                        link: '/abuse',
                    },
                    {
                        name: t('admin.menu.ops.parameters'),
                        app: 'admin',
                        link: '/settings',
                    },
                    {
                        name: t('admin.menu.ops.session'),
                        app: 'admin',
                        link: '/session-management',
                        roles: ['ROLE_SECURITY_TEAM'],
                    },
                    {
                        name: t('admin.menu.ops.loggers'),
                        app: 'admin',
                        link: '/loggers',
                        roles: ['ROLE_TECHNICAL_SUPPORT'],
                    },
                    {
                        name: t('admin.menu.ops.sso'),
                        app: 'sso',
                        link: '/organizations',
                    },
                    {
                        name: t('admin.menu.ops.globalidsearch'),
                        app: 'admin',
                        link: '/globalidsearch',
                    },
                    {
                        name: t('admin.menu.ops.company.updates.audit.logs'),
                        app: 'admin',
                        link: '/companies-audit-logs',
                    },
                ]),
            },
            {
                name: 'Shake (CRM)',
                links: [
                    {
                        name: 'Shake',
                        link: '/crm/app',
                    },
                    {
                        name: 'Shake Admin',
                        link: '/crm/app/engine',
                    },
                ].concat(
                    sortedByName([
                        {
                            name: 'Client Settings Organization Admin',
                            link: '/client-settings/admin/indices',
                        },
                        {
                            name: t('admin.menu.crm.rules'),
                            app: 'rules-engine',
                            link: '/rules',
                        },
                        {
                            name: t('admin.menu.crm.scoring'),
                            app: 'rules-engine',
                            link: '/scoring',
                        },
                        {
                            name: t('admin.menu.crm.tests'),
                            app: 'rules-engine',
                            link: '/tests',
                        },
                        {
                            name: t('admin.menu.crm.variables'),
                            app: 'rules-engine',
                            link: '/variables',
                        },
                        {
                            name: t('admin.menu.crm.mail.template'),
                            app: 'rules-engine',
                            link: '/templates',
                        },
                        {
                            name: 'Email qualification',
                            link: '/admin/email-qualification',
                        },
                        {
                            name: t('admin.menu.crm.mail.blacklist'),
                            link: '/rules-engine/blocklists/emails',
                        },
                        {
                            name: t('admin.menu.crm.auto.edr.blacklist'),
                            link: '/signup-client/admin/auto-edr',
                        },
                        {
                            name: t('admin.menu.crm.words.blacklist'),
                            link: '/rules-engine/blocklists/expressions',
                        },
                        {
                            name: t('admin.menu.managers.attribution'),
                            link: '/admin/manager/manager-attribution',
                            roles: ['ROLE_ADMIN_MANAGER'],
                        },
                        {
                            name: t('admin.menu.managers.rc.groups'),
                            link: '/rules-engine/sourcing-manager-groups',
                        },
                        {
                            name: 'Sourcing manager leaves',
                            link: '/rules-engine/sourcing-manager-leaves',
                            roles: ['ROLE_ADMIN_MANAGER'],
                        },
                        {
                            name: 'User report reasons',
                            link: '/admin/user-reports',
                            roles: ['ROLE_REPORT_ADMIN'],
                        },
                    ]),
                ),
            },
            {
                name: 'Mobile',
                links: sortedByName([
                    {
                        name: t('admin.menu.mobile.version'),
                        app: 'admin',
                        link: '/mobile',
                        roles: ['ROLE_MOBILE_ADMIN'],
                    },
                ]),
            },
            {
                name: t('admin.menu.freelancers'),
                links: sortedByName([
                    {
                        name: t('admin.menu.freelance.super.malters'),
                        app: 'admin',
                        link: '/loyalty',
                    },
                    {
                        name: t('admin.menu.freelance.brp'),
                        link: '/business-referral/eligibility',
                    },
                    {
                        name: t('admin.menu.freelance.company.logos'),
                        link: '/profile/admin/company-logos',
                    },
                    {
                        name: t('admin.menu.freelance.signup.workflows'),
                        link: '/profile/admin/signup-workflows',
                    },
                    {
                        name: 'Subcontractors companies',
                        link: '/admin/company/subcontractors',
                    },
                ]),
            },
        ];

        for (const section of sections) {
            const links: Link[] = [];
            for (const link of section.links) {
                const isLinkVisible = await useHasEnoughPriviledge(link.roles || []);
                if (isLinkVisible) {
                    links.push(link);
                }
            }
            filteredSections.value.push({...section, links});
        }
    });

    function sortedByName(links: Link[]) {
        return links.sort((l1, l2) => l1.name.localeCompare(l2.name));
    }
</script>

<style scoped lang="scss">
    .navbar {
        width: 100%;
        background-color: var(--joy-color-neutral-10);
        padding-left: 30px;
        padding-right: 10px;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .logo {
            display: flex;
            flex-direction: column;

            span {
                color: var(--joy-color-quaternary-50);
                font-size: var(--joy-font-size-primary-200);
                text-align: end;
                margin-right: 20px;
            }
        }

        .nav-links {
            list-style: none;
            display: flex;
        }

        .nav-item a {
            display: block;
            padding: 10px 15px;
            text-decoration: none;
            color: var(--joy-color-text-default);
        }

        .nav-item:hover a {
            color: var(--joy-color-text-default);
        }

        .logo img {
            width: 175px;
            vertical-align: middle;
        }
    }

    .nav-container {
        box-shadow: var(--joy-core-elevation-1);
        font-size: var(--joy-font-size-primary-300);
        position: relative;
        z-index: 1051; // VJoyAdminBanner has a 1050 z-index and can conflicts with navbar
    }

    .dropdown {
        background-color: var(--joy-color-neutral-10);
        z-index: 2;
        border: 1px solid var(--joy-color-neutral-30);
        border-radius: var(--joy-core-radius-3);
        margin-top: 15px;
        overflow: hidden;

        > li {
            width: 100%;
            white-space: nowrap;
            padding: 5px;

            &:not(.separator):hover,
            &:not(.separator):focus-within {
                background-color: var(--joy-color-neutral-30);
                cursor: pointer;
            }

            &.separator {
                cursor: default;

                hr {
                    margin: 0 1rem;
                }
            }
        }
    }

    a {
        text-decoration: none;
        color: var(--joy-color-text-default);
        font-weight: var(--joy-font-weight-normal);
        font-family: var(--joy-font-family-base);

        &.with-caret {
            &::after {
                content: '\25BF';
                margin-left: 0.5em;
            }
        }
    }

    ul {
        list-style: none;
        margin: 0;
        padding-left: 0;

        > li > ul {
            visibility: hidden;
            opacity: 0;
            min-width: 5rem;
            position: absolute;
            margin-top: 1rem;
            left: 0;
            display: none;
        }

        > li:hover > ul,
        > li:focus-within > ul,
        > li > ul:hover,
        > li > ul:focus {
            visibility: visible;
            opacity: 1;
            display: block;
        }
    }

    li {
        display: inline-block;
        padding: 1rem;
        position: relative;
        text-decoration: none;

        &:hover,
        &:focus-within {
            cursor: pointer;
        }
    }
</style>
<docs lang="md">
This component could be included in your application to add the admin menu. Typically it's used in a layout component
like this:

```vue
<template>
    <div>
        <AdminMenu app="admin" :logout="logout" />
        <slot />
    </div>
</template>

<script setup lang="ts">
    import {AdminMenu} from '#components';
    import {useAuth} from '#imports';

    const {logout} = useAuth();
</script>
```
</docs>
